import { useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useToast } from "../../util/ToastProvider";
import { useNavigate } from "react-router-dom";
import { trackEvent } from "../../util/analytics";
import { GoogleAuthProvider, signInWithCustomToken, signInWithPopup } from "firebase/auth";
import { AppUser } from "../../types/User";
import { setUser } from "../../store/slices/authSlice";
import { auth, functions } from "../../config/firebaseConfig";
import { useDispatch } from "react-redux";

export default function SignupPage() {
  const [email, setEmail] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [password, setPassword] = useState("");
  const [errorStatus, setErrorStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const provider = new GoogleAuthProvider();

  const useToaster = useToast();
  const navigate = useNavigate();

  // ✅ Email + password validation
  const isValidEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  function isPasswordComplex(password: string) {
    return (
      password.length >= 8 &&
      /[A-Z]/.test(password) &&
      /[a-z]/.test(password) &&
      /[0-9]/.test(password) &&
      /[^A-Za-z0-9]/.test(password)
    );
  }

  const passwordsMatch =
  password.length > 0 &&
  confirmPassword.length > 0 &&
  password === confirmPassword;


  const canSubmit =
  isValidEmail(email) &&
  isPasswordComplex(password) &&
  password === confirmPassword &&
  !loading;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorStatus(null);
    setLoading(true);

    try {
      const functions = getFunctions();

      if (password !== confirmPassword) {
        setErrorStatus("Passwords do not match.");
        setLoading(false);
        return;
      }

      const createUser = httpsCallable(functions, "authUserHandler");
      const result: any = await createUser({ type: "create", email, password });
      

      trackEvent("sign_up_success", {
        screen: "login",
      }); 
      if (result.data.success) {
        useToaster.success("Email sent successfully");
        // 👇 Redirect to confirmation page with email as query param
        navigate(`/auth/await-email?type=verify&email=${encodeURIComponent(email)}`);
      }
    } catch (err: any) {
      console.error(err);
      setErrorStatus(err.message || "Could not create account.");
    } finally {
      setLoading(false);
    }
  }

  async function loginWithGoogle() {
    try {
        setLoading(true);   // ✅ START LOADING

        const result = await signInWithPopup(auth, provider);

        const idToken = await result.user.getIdToken();

        const googleLogin = httpsCallable(functions, "googleLoginUser");

        const resp: any = await googleLogin({ idToken });

        const { customToken, uid, email, role, basicInfoSetup, emailVerified } =
            resp.data;

        await signInWithCustomToken(auth, customToken);

        const user: AppUser = {
            uid,
            email,
            displayName: result.user.displayName,
            dob: null,
            emailVerified,
            basicInfoSetup,
            role,
        };

        dispatch(setUser(user));

        if (!basicInfoSetup && role === "user") navigate("/setup-profile");
        else navigate("/");

    } catch (error) {
        console.error("Google login error:", error);
    } finally {
        setLoading(false);  // ✅ STOP LOADING
    }
}

  return (
    <>
      <h1 className="text-dark-text text-[20px] font-medium">
        Sign up for a free account
      </h1>

      <button
  onClick={loginWithGoogle}
  disabled={loading}
  className="flex gap-3 justify-center items-center w-full border-2 rounded-xl mt-4 px-4 py-2.5 text-white text-sm font-medium
  transform transition duration-300 hover:scale-105
  disabled:opacity-50 disabled:cursor-not-allowed"
>
  <img className="h-6" src="/google-icon-logo.svg"/>

  {loading ? "Signing in..." : "Continue with Google"}
</button>

<div className="flex items-center w-full my-6">

                <div className="flex-1 border-t border-gray-300"></div>

                <span className="px-4 text-gray-300 text-sm font-medium">OR</span>

                <div className="flex-1 border-t border-gray-300"></div>

            </div>

      <form className="mt-6 space-y-6" onSubmit={handleSubmit} noValidate>
        {/* Email field */}
        <div>
          <label className="block text-white font-medium text-primary">
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`mt-2 block w-full rounded-xl border-0 ring-1 ring-inset ring-dark-border !placeholder:text-dark-text !bg-dark-background-secondary-medium px-3 py-2 !text-white !caret-white !outline-none !focus:outline-none focus:ring-2 !focus:ring-white" ${errorStatus?.includes("email") ? "ring-red-400 focus:ring-red-500" : "ring-slate-300 focus:ring-indigo-500"}`}
            placeholder="you@example.com"
            required
          />
          {email.length > 0 && !isValidEmail(email) && (
            <p className="mt-1 text-sm text-red-500 font-medium">
              Please enter a valid email address.
            </p>
          )}
        </div>

        {/* Password field */}
        <div>
          <label className="block text-white font-medium text-primary">
            Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-2 block w-full rounded-xl border-0 ring-1 ring-inset ring-dark-border !placeholder:text-dark-text !bg-dark-background-secondary-medium px-3 py-2 !text-white !caret-white !outline-none !focus:outline-none focus:ring-2 !focus:ring-white"
            placeholder="Password"
            autoComplete="new-password"
            required
          />
          {password.length > 0 && password.length < 8 && (
            <p className="mt-1 text-sm text-red-500 font-medium">
              Minimum 8 characters required.
            </p>
          )}
          {password.length >= 8 && !isPasswordComplex(password) && (
            <p className="mt-1 text-sm text-red-500 font-medium">
              Password must include at least one uppercase, one lowercase, one
              number, and one special character.
            </p>
          )}
        </div>


        

      <div>
        <label className="block text-white font-medium text-primary">
          Confirm Password
        </label>
        <input
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className={`mt-2 block w-full rounded-xl border-0 ring-1 ring-inset
            !placeholder:text-dark-text !bg-dark-background-secondary-medium px-3 py-2
            !text-white !caret-white !outline-none focus:ring-2
            ${
              confirmPassword.length > 0 && password !== confirmPassword
                ? "ring-red-400 focus:ring-red-500"
                : "ring-dark-border focus:ring-white"
            }`}
          placeholder="Re-enter password"
          autoComplete="new-password"
          required
        />

        {confirmPassword.length > 0 && password !== confirmPassword && (
          <p className="mt-1 text-sm text-red-500 font-medium">
            Passwords do not match.
          </p>
        )}
      </div>

      {/* Error message */}
      {errorStatus && (
          <p className="text-sm text-red-600 font-medium">{errorStatus}</p>
      )}

        {/* Submit button */}
        <button
          type="submit"
          disabled={loading || !canSubmit || !isPasswordComplex(password)}
          className="w-full rounded-xl bg-indigo-600 px-4 py-2.5 text-white text-sm font-medium 
                     hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Creating Account..." : "Create account"}
        </button>
      </form>

      <div className="mt-4 text-dark-text text-[12px] font-normal">
        You will receive an email with an account activation link. Please check
        your spam folder if you don’t see it in your inbox.
      </div>

      <div className="mt-6 text-sm text-white text-center sm:text-left align-start leading-6">
        Already have an account?{" "}
        <a href="/auth/login" className="underline">
          Sign in
        </a>
      </div>
    </>
  );
}
